'use client';
import { ParsedUrlQuery } from 'querystring';

import React, { useEffect, useMemo, useState } from 'react';

import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useParams, useSearchParams } from 'next/navigation';
import dynamic from 'next/dynamic';

import { callService } from '@api/remote-call';
import { formatArrayToString } from '@utils/utils';
import { useTimer } from '@contexts/timer/TimerContext';
import { paramsToObject } from '@components/views/utils';
import {
  ResourceIdProvider,
  SearchURLProvider,
  SsrDataProvider,
} from '@api/data-loaders/resource';
import { IsKocAliemValidatedProvider } from '@contexts/loyalty/KocAilem';
import { useSkipEffectOnMount } from '@hooks/lifecycle';
import GeneralLoader from '@components/ui/GeneralLoader';

import HotelProvider from '../../hotel/detail/context';
import PackageDetail from './PackageDetail';
import { PackageContainer } from './styled';

const HotelDetail = dynamic(() => import('./package-hotel-tab'));

interface PackageDetailScreenProps {
  tabType?: string;
  metaInfo?: Record<string, string>;
  data?: any;
  initialData?: any;
  packageInitialData?: any;
}

const PackageDetailScreen = ({
  tabType,
  metaInfo,
  data,
  initialData,
  packageInitialData,
}: PackageDetailScreenProps) => {
  const [isClientSide, setIsClientSide] = useState(false);
  const [selectedTab, setSelectedTab] = useState('package');
  const [showFetchingAnimation, setShowFetchingAnimation] = useState(false);
  const [shouldResearchPackage, setShouldResearchPackage] = useState(false);
  const [isReloading, setIsReloading] = useState(false);

  const [showErrorModalPackageTimer, setShowErrorModalPackageTimer] =
    useState(false);
  const [showErrorModalHotelTimer, setShowErrorModalHotelTimer] =
    useState(false);
  const searchParams = useSearchParams();
  const query: ParsedUrlQuery = useMemo(() => {
    return paramsToObject(searchParams?.entries());
  }, [searchParams]);

  const params = useParams();

  useSkipEffectOnMount(() => {
    if (query && !isClientSide) {
      setIsClientSide(true);
    }
  }, [query]);

  const {
    timers,
    addTimer,
    removeTimer,
    subscribeTimerFinished,
    unsubscribeTimerFinished,
  } = useTimer();

  const handleAddTimer = (id: string) => {
    if (id) {
      addTimer(id);
    }
  };

  useEffect(() => {
    if (selectedTab === 'hotel') {
      setShouldResearchPackage(true);
    }
  }, [selectedTab]);
  useEffect(() => {
    if (tabType) {
      setSelectedTab(tabType);
    }
  }, [tabType]);

  const {
    in: startDate,
    out: endDate,
    room = ['2'],
    dep,
    depIsCity,
    inboundFlightNo,
    outboundFlightNo,
    lastFlightDate,
  } = query;

  const fetchPackageDetails = () => {
    const formattedRoom = room
      ? Array.isArray(room)
        ? room.map((item) => item.replaceAll('_', ','))
        : [room.replaceAll('_', ',')]
      : [];
    return callService(
      { service: 'PackagesService', method: 'getPackage', version: 'v3' },
      {
        slug: String(params?.slug),
        body: {
          room: formattedRoom,
          in: formatArrayToString(startDate ?? ''),
          out: formatArrayToString(endDate ?? ''),
          departure: String(dep),
          arrival: 'ECN',
          oneWay: false,
          direct: false,
          departureIsCity: depIsCity ? Boolean(depIsCity) : false,
          arrivalIsCity: false,
          inboundFlightNo: inboundFlightNo as string,
          outboundFlightNo: outboundFlightNo as string,
          lastFlightDate: lastFlightDate as string,
        },
      }
    );
  };

  const {
    data: packageData,
    refetch: refetchPackage,
    isError: isGetPackageDetailError,
    isInitialLoading: isPackageLoading,
    isFetching: isPackageFetching,
    isSuccess: isPackageDataLoadingSuccess,
  } = useQuery(['packageDetail', JSON.stringify(query)], fetchPackageDetails, {
    enabled: Boolean(params?.slug && isClientSide),
    initialData: packageInitialData,
    onSuccess: () => {
      setShowFetchingAnimation(false);
    },
  });
  const queryClient = useQueryClient();
  useEffect(() => {
    if (initialData) {
      queryClient.setQueryData(
        ['hotelDetail', JSON.stringify(query)],
        initialData
      );
    }
  }, [initialData, query, queryClient]);

  const fetchHotelDetails = () => {
    const basketId =
      typeof window !== 'undefined' && localStorage.getItem('basketId');
    const formattedRoom = query
      ? Array.isArray(query.room)
        ? query.room.map((item) => item.replaceAll('_', ','))
        : typeof query.room === 'string'
        ? [query.room.replaceAll('_', ',')]
        : []
      : [];
    return callService(
      { service: 'HotelsService', method: 'getHotel', version: 'v3' },
      {
        slug: String(params?.slug),
        room: formattedRoom,
        ...(startDate && endDate
          ? {
              _in: formatArrayToString(startDate),
              out: formatArrayToString(endDate),
            }
          : {}),
        ...(basketId ? { basketId } : {}),
      }
    );
  };
  const {
    data: hotelData,
    refetch: refetchHotel,
    isError: isGetHotelDetailError,
    isInitialLoading: isHotelLoading,
    isFetching: isHotelFetching,
    isSuccess: isHotelDataFetchingSuccess,
  } = useQuery(['hotelDetail', JSON.stringify(query)], fetchHotelDetails, {
    enabled: Boolean(params?.slug && isClientSide),
    initialData: initialData,
  });

  useEffect(() => {
    if (isHotelDataFetchingSuccess) {
      handleAddTimer(
        hotelData?.rooms?.[0]?.providerInfo?.akgunlerProviderInfo
          ?.shoppingFileId ?? ''
      );
    }
  }, [isHotelDataFetchingSuccess]);

  useEffect(() => {
    if (isPackageDataLoadingSuccess) {
      handleAddTimer(packageData?.shoppingFileId ?? '');
    }
  }, [isPackageDataLoadingSuccess]);

  useEffect(() => {
    if (
      hotelData?.rooms?.[0]?.providerInfo?.akgunlerProviderInfo?.shoppingFileId
    ) {
      const listenerId = `listener${hotelData?.rooms?.[0]?.providerInfo?.akgunlerProviderInfo?.shoppingFileId}`;
      subscribeTimerFinished(
        listenerId,
        hotelData?.rooms?.[0].providerInfo?.akgunlerProviderInfo
          ?.shoppingFileId,
        handleTimerFinishedOnlyHotel
      );
      return () => {
        unsubscribeTimerFinished(listenerId);
      };
    }
  }, [hotelData]);

  useEffect(() => {
    if (packageData?.shoppingFileId) {
      const listenerId = `listener${packageData.shoppingFileId}`;
      subscribeTimerFinished(
        listenerId,
        packageData.shoppingFileId,
        handleTimerFinished
      );
      return () => {
        unsubscribeTimerFinished(listenerId);
      };
    }
  }, [packageData]);

  const handleTimerFinishedOnlyHotel = (timerId) => {
    setShowErrorModalHotelTimer(true);
    removeTimer(timerId);
  };

  const handleTimerFinished = (timerId) => {
    setShowErrorModalPackageTimer(true);
    removeTimer(timerId);
  };

  const backToDetail = () => {
    setIsReloading(true);
    setShowErrorModalHotelTimer(false);
    setShowErrorModalPackageTimer(false);
    window.location.reload();
  };
  useEffect(() => {
    if (shouldResearchPackage && packageData?.shoppingFileId) {
      removeTimer(packageData.shoppingFileId);
    }
  }, [shouldResearchPackage]);
  return (
    <ResourceIdProvider value={data?.props?.resourceKey}>
      <SsrDataProvider value={data}>
        <SearchURLProvider value={data?.props?.urlSearch}>
          <IsKocAliemValidatedProvider>
            <PackageContainer>
              {isReloading ? <GeneralLoader /> : null}
              {selectedTab === 'package' ? (
                <HotelProvider>
                  <PackageDetail
                    selectedTab={selectedTab}
                    setSelectedTab={setSelectedTab}
                    packageData={packageData}
                    refetchPackage={refetchPackage}
                    isGetPackageDetailError={isGetPackageDetailError}
                    isPackageLoading={isPackageLoading}
                    isPackageFetching={isPackageFetching}
                    showFetchingAnimation={showFetchingAnimation}
                    setShowFetchingAnimation={setShowFetchingAnimation}
                    shouldResearchPackage={shouldResearchPackage}
                    setShouldResearchPackage={setShouldResearchPackage}
                    onlyHotelData={hotelData}
                    metaInfo={metaInfo}
                    timers={timers}
                    showErrorModalPackageTimer={showErrorModalPackageTimer}
                    backToDetail={backToDetail}
                  />
                </HotelProvider>
              ) : (
                <HotelProvider>
                  <HotelDetail
                    selectedTab={selectedTab}
                    setSelectedTab={setSelectedTab}
                    data={hotelData}
                    refetch={refetchHotel}
                    isGetHotelDetailError={isGetHotelDetailError}
                    isLoading={isHotelLoading}
                    isHotelFetching={isHotelFetching}
                    metaInfo={metaInfo}
                    timers={timers}
                    showErrorModalHotelTimer={showErrorModalHotelTimer}
                    backToDetail={backToDetail}
                    isHotelDataFetchingSuccess={isHotelDataFetchingSuccess}
                  />
                </HotelProvider>
              )}
            </PackageContainer>
          </IsKocAliemValidatedProvider>
        </SearchURLProvider>
      </SsrDataProvider>
    </ResourceIdProvider>
  );
};

export default PackageDetailScreen;
